import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftTypography from 'components/SoftTypography';
import SoftInput from 'components/SoftInput';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { AccountingDocumentRegisterService } from 'services/accounting-document-register';
import { formatVndIntegerValue } from 'utils';
import toast from 'react-hot-toast';

const currentDate = new Date();
const defaultYear = String(currentDate.getFullYear());
const defaultMonth = String(currentDate.getMonth() + 1).padStart(2, '0');

const monthOptions = [
  { label: 'Tất cả tháng', value: '' },
  ...Array.from({ length: 12 }).map((_, index) => {
    const month = String(index + 1).padStart(2, '0');
    return { label: `Tháng ${month}`, value: month };
  }),
];

const ui = {
  title: { fontSize: 20, lineHeight: 1.2 },
  label: { fontSize: 14, lineHeight: 1.2 },
  table: { fontSize: 13 },
  tableHeader: { border: '1px solid #000', padding: 6, textAlign: 'center', fontSize: 13 },
  tableCellCenter: { border: '1px solid #000', padding: 6, textAlign: 'center', fontSize: 13 },
  tableCellRight: { border: '1px solid #000', padding: 6, textAlign: 'right', fontSize: 13 },
};

const buildPrintTemplate = ({ filters, rows, summary }) => {
  const fmt = (value) => new Intl.NumberFormat('vi-VN').format(Number(value || 0));

  const rowHtml = rows
    .map(
      (item, index) => `
      <tr>
        <td class="cell center">${index + 1}</td>
        <td class="cell center">${item.voucherNumber || ''}</td>
        <td class="cell center">${item.voucherDate || ''}</td>
        <td class="cell right">${fmt(item.amount)}</td>
      </tr>
    `,
    )
    .join('');

  return `
  <html>
    <head>
      <meta charset="UTF-8" />
      <title>Sổ Đăng Ký Chứng Từ Ghi Sổ</title>
      <style>
        body { font-family: 'Times New Roman', serif; color: #000; margin: 24px; font-size: 14px; }
        .header { display: flex; justify-content: space-between; margin-bottom: 16px; }
        .title { text-align: center; margin: 8px 0 12px; }
        .title h1 { margin: 0; font-size: 24px; text-transform: uppercase; }
        .subtitle { margin-top: 6px; }
        table { width: 100%; border-collapse: collapse; }
        .cell, .head { border: 1px solid #000; padding: 6px; }
        .head { text-align: center; font-weight: 700; }
        .center { text-align: center; }
        .right { text-align: right; }
        .summary { margin-top: 10px; width: 360px; margin-left: auto; }
        .summary td { padding: 4px 6px; }
        .sign { margin-top: 48px; display: flex; justify-content: space-between; text-align: center; align-items: flex-start; }
        .sign-col { width: 32%; }
        .sign-date-row { min-height: 20px; margin-bottom: 2px; }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <div><strong>Đơn vị:</strong> .....................................</div>
          <div><strong>Địa chỉ:</strong> .....................................</div>
        </div>
        <div class="center">
          <div><strong>Mẫu số S02b-DN</strong></div>
          <div><em>(Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</em></div>
        </div>
      </div>

      <div class="title">
        <h1>Sổ Đăng Ký Chứng Từ Ghi Sổ</h1>
        <div class="subtitle">Năm: ${filters.year} - Tháng: ${summary.month ? String(summary.month).padStart(2, '0') : ''}</div>
      </div>

      <table>
        <thead>
          <tr>
            <th class="head" style="width: 10%">STT</th>
            <th class="head" style="width: 26%">Chứng từ ghi sổ<br/>Số hiệu (A)</th>
            <th class="head" style="width: 24%">Ngày, tháng (B)</th>
            <th class="head" style="width: 40%">Số tiền (1)</th>
          </tr>
        </thead>
        <tbody>
          ${rowHtml || '<tr><td class="cell center" colspan="4">Không có dữ liệu</td></tr>'}
        </tbody>
      </table>

      <table class="summary">
        <tr><td><strong>Cộng tháng:</strong></td><td class="right"><strong>${fmt(summary.monthlyTotal)}</strong></td></tr>
        <tr><td><strong>Cộng lũy kế từ đầu quý:</strong></td><td class="right"><strong>${fmt(summary.quarterAccumulatedTotal)}</strong></td></tr>
      </table>

      <div class="sign">
        <div class="sign-col">
          <div class="sign-date-row">&nbsp;</div>
          <div><strong>Người ghi sổ</strong></div>
          <div><em>(Ký, họ tên)</em></div>
        </div>
        <div class="sign-col">
          <div class="sign-date-row">&nbsp;</div>
          <div><strong>Kế toán trưởng</strong></div>
          <div><em>(Ký, họ tên)</em></div>
        </div>
        <div class="sign-col">
          <div class="sign-date-row"><em>Ngày ...... tháng ...... năm ......</em></div>
          <div><strong>Người đại diện theo pháp luật</strong></div>
          <div><em>(Ký, họ tên, đóng dấu)</em></div>
        </div>
      </div>
    </body>
  </html>`;
};

export default function AccountingDocumentRegister() {
  const location = useLocation();
  const navigate = useNavigate();
  const isView = location.pathname?.includes('/view');
  const isEdit = location.pathname?.includes('/edit');
  const isCreate = location.pathname?.includes('/create');
  const canEditFilter = !isView;

  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({
    year: defaultYear,
    month: defaultMonth,
    searchBy: '',
    page: 1,
    limit: 1000,
    sortBy: 'voucherDate',
    sortType: 'asc',
  });

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    year: Number(defaultYear),
    month: Number(defaultMonth),
    quarter: 1,
    monthlyTotal: 0,
    quarterAccumulatedTotal: 0,
  });
  const [reconciliation, setReconciliation] = useState({
    totalPostedAmount: 0,
    isBalanced: true,
    difference: 0,
  });
  const [paginate, setPaginate] = useState({ page: 1, size: 1000, count: 0 });

  const [formFilter, setFormFilter] = useState({
    year: defaultYear,
    month: defaultMonth,
    searchBy: '',
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || '');
    const year = searchParams.get('year') || defaultYear;
    const month = searchParams.get('month') || defaultMonth;
    const searchBy = searchParams.get('searchBy') || '';

    setQuery((prev) => ({
      ...prev,
      year,
      month,
      searchBy,
      page: 1,
      limit: 1000,
      sortBy: 'voucherDate',
      sortType: 'asc',
    }));

    setFormFilter({ year, month, searchBy });
  }, [location.search]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await AccountingDocumentRegisterService.getPaginate(query);
      const payload = response?.data || response || {};

      setRows(payload.items || []);
      setSummary(
        payload.summary || {
          year: Number(query.year),
          month: Number(query.month || defaultMonth),
          quarter: 1,
          monthlyTotal: 0,
          quarterAccumulatedTotal: 0,
        },
      );
      setReconciliation(
        payload.reconciliation || {
          totalPostedAmount: 0,
          isBalanced: true,
          difference: 0,
        },
      );
      setPaginate(
        payload.paginate || {
          page: Number(query.page || 1),
          size: Number(query.limit || 10),
          count: 0,
        },
      );
    } catch (error) {
      setRows([]);
      toast.error(error?.response?.data?.message || 'Không thể tải Sổ đăng ký chứng từ ghi sổ');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = useMemo(() => {
    const size = Number(paginate.size || 10);
    const count = Number(paginate.count || 0);
    if (!size) return 1;
    return Math.max(1, Math.ceil(count / size));
  }, [paginate.count, paginate.size]);

  const handleApplyFilter = () => {
    if (!/^\d{4}$/.test(String(formFilter.year || ''))) {
      toast.error('Năm không hợp lệ (YYYY)');
      return;
    }

    const month = String(formFilter.month || '');
    if (month && (Number(month) < 1 || Number(month) > 12)) {
      toast.error('Tháng không hợp lệ');
      return;
    }

    setQuery((prev) => ({
      ...prev,
      year: String(formFilter.year),
      month,
      searchBy: String(formFilter.searchBy || '').trim(),
      page: 1,
      limit: 1000,
    }));
  };

  const handleResetFilter = () => {
    const reset = { year: defaultYear, month: defaultMonth, searchBy: '' };
    setFormFilter(reset);
    setQuery((prev) => ({
      ...prev,
      ...reset,
      page: 1,
      limit: 1000,
      sortBy: 'voucherDate',
      sortType: 'asc',
    }));
  };

  const openViewByFilter = () => {
    const search = new URLSearchParams({
      year: String(formFilter.year || defaultYear),
      month: String(formFilter.month || ''),
      searchBy: String(formFilter.searchBy || ''),
    });

    navigate(`/templates/accounting-document-register/view?${search.toString()}`);
  };

  const handleSubmit = () => {
    if (!/^\d{4}$/.test(String(formFilter.year || ''))) {
      toast.error('Năm không hợp lệ (YYYY)');
      return;
    }
    openViewByFilter();
  };

  const handlePrint = () => {
    const htmlContent = buildPrintTemplate({ filters: formFilter, rows, summary });
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Trình duyệt đang chặn cửa sổ in. Vui lòng cho phép pop-up.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 200);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={11}>
            <Card>
              <SoftBox p={3} display="flex" flexDirection="column" minHeight="calc(100vh - 220px)">
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <SoftTypography variant="h5" fontWeight="bold" sx={ui.title}>
                    Sổ Đăng Ký Chứng Từ Ghi Sổ (S02b-DN)
                  </SoftTypography>
                </SoftBox>

                <SoftBox display="flex" gap={2} flexWrap="wrap" alignItems="flex-end" mb={2}>
                  <SoftBox width="8rem">
                    <SoftTypography variant="caption" fontWeight="bold" mb={0.5} sx={ui.label}>
                      Năm
                    </SoftTypography>
                    <SoftInput
                      value={formFilter.year}
                      onChange={(e) =>
                        setFormFilter((prev) => ({
                          ...prev,
                          year: e.target.value.replace(/\D/g, '').slice(0, 4),
                        }))
                      }
                      disabled={!canEditFilter}
                    />
                  </SoftBox>

                  <SoftBox width="12rem">
                    <SoftTypography variant="caption" fontWeight="bold" mb={0.5} sx={ui.label}>
                      Tháng
                    </SoftTypography>
                    <select
                      value={formFilter.month}
                      disabled={!canEditFilter}
                      onChange={(e) => setFormFilter((prev) => ({ ...prev, month: e.target.value }))}
                      style={{ width: '100%', height: 34, borderRadius: 8, border: '1px solid #d2d6da', padding: '0 8px', fontSize: 14 }}
                    >
                      {monthOptions.map((item) => (
                        <option key={item.value || 'all'} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </SoftBox>

                  <SoftBox width="16rem">
                    <SoftTypography variant="caption" fontWeight="bold" mb={0.5} sx={ui.label}>
                      Số hiệu CTGS
                    </SoftTypography>
                    <SoftInput
                      value={formFilter.searchBy}
                      disabled={!canEditFilter}
                      placeholder="Tìm theo số chứng từ"
                      onChange={(e) =>
                        setFormFilter((prev) => ({ ...prev, searchBy: e.target.value }))
                      }
                    />
                  </SoftBox>

                  {canEditFilter && (
                    <>
                      <SoftButton variant="outlined" color="info" onClick={handleApplyFilter} disabled={loading} sx={{ borderRadius: 0 }}>
                        Tải dữ liệu
                      </SoftButton>
                      <SoftButton variant="outlined" color="secondary" onClick={handleResetFilter} disabled={loading} sx={{ borderRadius: 0 }}>
                        Reset
                      </SoftButton>
                    </>
                  )}
                </SoftBox>

                <SoftBox mb={2}>
                  <span
                    style={{
                      display: 'inline-block',
                      borderRadius: 8,
                      padding: '6px 10px',
                      fontSize: 12,
                      fontWeight: 700,
                      color: reconciliation.isBalanced ? '#1b5e20' : '#b71c1c',
                      background: reconciliation.isBalanced ? '#e8f5e9' : '#ffebee',
                    }}
                  >
                    {reconciliation.isBalanced ? 'Khớp số liệu' : 'Lệch số liệu'}
                  </span>
                </SoftBox>

                <div style={{ overflowX: 'auto', border: '1px solid #ccc' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760, ...ui.table }}>
                    <thead>
                      <tr>
                        <th style={ui.tableHeader}>STT</th>
                        <th style={ui.tableHeader}>
                          Chứng từ ghi sổ<br />Số hiệu (A)
                        </th>
                        <th style={ui.tableHeader}>
                          Ngày, tháng (B)
                        </th>
                        <th style={ui.tableHeader}>Số tiền (1)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan={4} style={{ ...ui.tableCellCenter, padding: 10 }}>
                            Đang tải dữ liệu...
                          </td>
                        </tr>
                      )}

                      {!loading && !rows.length && (
                        <tr>
                          <td colSpan={4} style={{ ...ui.tableCellCenter, padding: 10 }}>
                            Không có dữ liệu
                          </td>
                        </tr>
                      )}

                      {!loading &&
                        rows.map((item, index) => (
                          <tr
                            key={item.id}
                            onClick={() => navigate(`/templates/accounting-vouchers/${item.id}/view`)}
                            style={{ cursor: 'pointer' }}
                            title="Mở chứng từ ghi sổ"
                          >
                            <td style={ui.tableCellCenter}>
                              {(paginate.page - 1) * paginate.size + index + 1}
                            </td>
                            <td style={ui.tableCellCenter}>{item.voucherNumber}</td>
                            <td style={ui.tableCellCenter}>{item.voucherDate}</td>
                            <td style={ui.tableCellRight}>
                              {formatVndIntegerValue(item.amount || 0)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <SoftBox mt={2} display="flex" justifyContent="flex-end">
                  <table style={{ width: 380 }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '4px 6px', fontWeight: 700 }}>Cộng tháng:</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700 }}>
                          {formatVndIntegerValue(summary.monthlyTotal || 0)}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '4px 6px', fontWeight: 700 }}>
                          Cộng lũy kế từ đầu quý (Q{summary.quarter || 1}):
                        </td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700 }}>
                          {formatVndIntegerValue(summary.quarterAccumulatedTotal || 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </SoftBox>

                <SoftBox mt={2} display="flex" justifyContent="space-between" alignItems="center">
                  <SoftTypography variant="caption" color="text">
                    Tổng số dòng: {paginate.count} {totalPages > 1 ? `- Trang ${paginate.page}/${totalPages}` : ''}
                  </SoftTypography>
                </SoftBox>

                <SoftBox mt="auto" pt={2} display="flex" justifyContent="flex-end" gap={1}>
                  <SoftButton variant="outlined" color="dark" onClick={() => navigate('/templates/accounting-document-register')}>
                    Quay lại
                  </SoftButton>
                  {isView ? (
                    <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                      In Bảng
                    </SoftButton>
                  ) : (
                    <SoftButton variant="gradient" color="dark" onClick={handleSubmit}>
                      {isEdit ? 'Lưu' : isCreate ? 'Tạo' : 'Xem'}
                    </SoftButton>
                  )}
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
